<template>
  <div class="status-container">
    <div class="application-actions">
      <PermissionsWrapper
        :showDisabledIfNotVisible="true"
        :permissionIds="[Permissions.ProgramConfiguration]"
        :permissionLevel="PermissionLevels.Update"
      >
        <application-status />
      </PermissionsWrapper>
      <PermissionsButton
        v-if="isRTC"
        :permissionIds="[Permissions.RightToCancel]"
        :permissionLevel="PermissionLevels.Update"
        @click="showCancelModal = true"
        class="cancel-application-btn"
      >
        Cancel Application
      </PermissionsButton>
      <div
        class="xpage-top-details-status"
        v-auto-animate="{ ...$animate, duration: 100 }"
      >
        <button
         	v-if="applicationStore.viewModel.showMoveToLoanService"
          type="submit"
          @click="moveToLoanService(true)"
          class="loan-service-btn"
					:disabled="userCanMoveAppToLoanService === false"
        >
          <span v-if="applicationStore.viewModel.showMoveToLoanService">Move To Loan Service »</span>
          <span v-else>Processing...</span>
        </button>
      </div>
    </div>
  </div>
  <CancellationModal
    v-if="showCancelModal"
    @update="updateApplication"
    @close="showCancelModal = $event"
  />
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { storeToRefs } from "pinia";
import { useApplicationStore } from "@/store/store/applicationVMStore";
import { Permissions, PermissionLevels } from "@/models/permissions";
import PermissionsWrapper from "@/components/wrappers/PermissionsWrapper.vue";
import PermissionsButton from "@/components/wrappers/PermissionsButton.vue";
import ApplicationStatus from "@/components/v2/application/ApplicationStatus.vue";
import CancellationModal from "@/components/v2/application/refi/CancellationModal.vue";
import functions from "@/use/functions";
import axios from "axios";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { ProductIds } from "@/types/types";
import { useRolesAccess } from '@/composables/usePermissionsAccess';
import { Roles } from '@/models/permissions';

interface ErrorResponseData {
  message?: string;
  errors?: string[];
}

const rootStore = useStore();

const applicationStore = useApplicationStore();

const router = useRouter();

const { summary } = storeToRefs(applicationStore);
const { inRole } = useRolesAccess();

const isRTC = computed<boolean>(() => summary.value.isRightToCancel);

const showCancelModal = ref<boolean>(false);

const oidcUser = computed(() => rootStore.getters["oidcStore/user"]);

const updateApplication = async () => {
  const referenceId = applicationStore.referenceId;
  await Promise.all([
    applicationStore.getApplication(referenceId),
    applicationStore.getApplicationSummary(referenceId),
    applicationStore.getApplicationComments(referenceId),
  ]);
};

const isAdmin = computed<boolean>(() => inRole(Roles.Admin));
const userCanMoveAppToLoanService = computed<boolean>(() => (applicationStore.viewModel.productTypeId === ProductIds.PEL && isAdmin.value) || applicationStore.viewModel.productTypeId !== ProductIds.PEL);

async function moveToLoanService(confirmation) {

  if (confirmation) {
    functions.openModal({
      title: "Confirm",
      description:
        "Are you sure you want to move this application to Loan Service?",
      buttons: [
        { title: "Cancel", onClick: () => functions.closeModal() },
        { title: "Submit", onClick: () => moveToLoanService(true) },
      ],
    });
  } else {
    functions.closeModal();

    try {
      await axios.post(
        `/api/applications/${applicationStore.applicationId}/migrate`,
        {
          AuthorizedByUserId: oidcUser.value?.sub,
          MigrateApplicationTargets: applicationStore.viewModel.id,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      functions.openModal({
        title: "Success",
        description: "Application has been successfully moved to Loan Service",
        buttons: [
          {
            title: "Close",
            onClick: () => {
              functions.closeModal();
              // refresh the page
              router.go(0);
            },
          },
        ],
      });
    } catch (error) {
      console.error("Error moving application to loan service", error);

      const modal = {
        title: "Error",
        description:
          "There was an issue moving this application to Loan Service",
        html: "",
      };

      if (axios.isAxiosError(error)) {
        const data = error.response?.data as ErrorResponseData;

        if (data) {
          if (data.message) {
            modal.description = data.message;
          }
          if (data.errors?.length) {
            modal.html = `<ul>${data.errors
              .map((e: string) => `<li>${e}</li>`)
              .join("")}</ul>`;
          }
        } else {
          modal.description = error.message;
        }
      } else if (error instanceof Error) {
        modal.description = error.message;
      } else {
        modal.description = "An unknown error occurred";
      }
      functions.openModal(modal);
    }
  }
}
</script>

<style scoped>
.status-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cancel-modal {
  background-color: white;
  padding: 20px;
  border-radius: 5px;
  width: 640px;
  height: 460px;
  text-align: center;
  box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.2);
}

.xpage-top-details-status {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.loan-service-btn {
  background-color: #48a842;
  color: white;
  min-width: 160px;
}

.loan-service-btn:hover {
  background: #0ecb8e;
}

.cancel-application-btn,
.loan-service-btn {
  padding: 0 12px;
  font-weight: 600;
  border: none;
  line-height: 26px;
  cursor: pointer;
  border-radius: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.cancel-application-btn {
  margin-top: 5px;
  background-color: black;
  color: white;
}
.application-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 5px;
  position: relative;
  border-radius: 5px;
  z-index: 1;
}

@media screen and (max-width: 768px) {
  .cancel-application-btn,
  .loan-service-btn {
    width: 100%;
  }

  .application-actions {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
